import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { Box, Button, Checkbox, darken, FormControlLabel, IconButton, LinearProgress, Modal, Tooltip, Typography } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Fulfillment, Profile, UserProfile } from 'api';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { ModelTeamSwitch } from 'dashboard409a/components/ModelTeamSwitch';
import { getCalendarColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import { NO_PREPARERS_ASSIGNED, NO_REVIEWERS_ASSIGNED } from 'dashboard409a/pages/Operations/components/constants';
import PersonnelRoleCard from 'dashboard409a/pages/Operations/components/ValuedInformation/PersonnelRoleCard';
import ValuationStatusSelect from 'dashboard409a/pages/Operations/components/ValuedInformation/ValuationStatusSelect';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { FulfillmentQueryKeys } from 'dashboard409a/services/fulfillment';
import { ModelsQueryKeys } from 'dashboard409a/services/models';
import { RootStateType } from 'dashboard409a/states/store';
import { AssignUsersDialog } from './components';
import { FulfillmentListResponse, modelStatusMap, reverseModelStatusMap } from './ModelStatus.constants';
import { ModelStatusProps } from './ModelStatus.props';
import {
  BlurOverlay,
  BlurredContent,
  Container,
  CreateModelSupportButton,
  DateRow,
  DeleteModalActions,
  DeleteModalCloseButtonContainer,
  DeleteModalContainer,
  FooterBox,
  OverlayContainer,
  SpacedContainer,
  StatusRow,
  StyledBox,
} from './ModelStatus.styles';

const ModelStatus = ({
  fulfillmentData,
  isLoading,
  onCreateModelSupport,
  isCreatingModelSupport = false,
  companyId,
  valuationId,
  companyMeasurementDateId,
  readOnly = false,
  isSlcModel,
  onSlcModelChange,
}: ModelStatusProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [users, setUsers] = useState<{ reviewers: UserProfile[]; preparers: UserProfile[] }>({
    reviewers: [],
    preparers: [],
  });
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingModelSupport, setIsDeletingModelSupport] = useState(false);
  const reviewerButtonRef = useRef<HTMLButtonElement>(null);
  const preparerButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorButtonRef, setAnchorButtonRef] = useState<HTMLButtonElement | null>(null);

  // Get the current fulfillment data that matches the company measurement date ID
  const currentFulfillment = useMemo(() => {
    if (!fulfillmentData?.results) {
      return undefined;
    }

    // If we have a specific company measurement date ID, find the matching fulfillment
    if (companyMeasurementDateId) {
      return fulfillmentData.results.find(
        fulfillment => fulfillment.company_measurement_date === companyMeasurementDateId
      );
    }

    // Fallback to first fulfillment
    return fulfillmentData.results[0];
  }, [fulfillmentData, companyMeasurementDateId]);

  // Determine if model support exists based on whether fulfillment exists and has model_status
  const hasModelSupport = useMemo(
    () => !!currentFulfillment && currentFulfillment.model_status !== undefined,
    [currentFulfillment]
  );

  useEffect(() => {
    if (currentFulfillment) {
      setUsers({
        reviewers: (currentFulfillment.reviewers as UserProfile[]) || [],
        preparers: (currentFulfillment.preparers as UserProfile[]) || [],
      });
    }
  }, [currentFulfillment]);

  // Helper function to get user display name
  const getUserDisplayName = useCallback(
    (user: UserProfile): string =>
      user.first_name ? `${user.first_name} ${user.last_name}` : user.email || 'Unknown User',
    []
  );

  const [tempFormData, setTempFormData] = useState<Fulfillment>(
    () =>
      ({
        ...currentFulfillment,
        model_status: currentFulfillment?.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
        is_waiting: currentFulfillment?.is_waiting ?? false,
      }) as Fulfillment
  );

  // Sync tempFormData with currentFulfillment when it changes
  useEffect(() => {
    if (currentFulfillment) {
      setTempFormData({
        ...currentFulfillment,
        model_status: currentFulfillment.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
        is_waiting: currentFulfillment.is_waiting ?? false,
      });
    }
  }, [currentFulfillment]);

  // Determine if fields should be disabled (when is_waiting is true)
  const isWaiting = tempFormData.is_waiting ?? false;
  const fieldsDisabled = readOnly || isWaiting;

  const isSuperuser = useSelector((state: RootStateType) => state.user.is_superuser);

  // Helper function to update both caches with the updated fulfillment data
  const updateCaches = useCallback(
    (
      updatedFulfillmentData: Partial<Fulfillment> & {
        reviewerUsers?: UserProfile[];
        preparerUsers?: UserProfile[];
      }
    ) => {
      const { reviewerUsers, preparerUsers, ...updateFields } = updatedFulfillmentData;

      // Helper to build the updated fulfillment object
      const buildUpdatedFulfillment = (fulfillment: Fulfillment) => {
        // If we have full user objects provided, use them directly
        // Otherwise, keep the existing reviewers/preparers from the fulfillment
        const updatedReviewers = reviewerUsers || fulfillment.reviewers;

        const updatedPreparers = preparerUsers || fulfillment.preparers;

        return {
          ...fulfillment,
          ...updateFields,
          reviewers: updatedReviewers,
          preparers: updatedPreparers,
        };
      };

      // Update the fulfillment list cache (used by ModelStatus in the Modal)
      queryClient.setQueriesData(
        { queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId], exact: false },
        (oldData: FulfillmentListResponse) => {
          if (!oldData?.results || !Array.isArray(oldData.results)) {
            return oldData;
          }

          const updatedResults = oldData.results.map((fulfillment: Fulfillment) => {
            if (
              fulfillment.id === currentFulfillment?.id ||
              fulfillment.company_measurement_date === currentFulfillment?.company_measurement_date
            ) {
              return buildUpdatedFulfillment(fulfillment);
            }
            return fulfillment;
          });

          return {
            ...oldData,
            results: updatedResults,
          };
        }
      );

      // Update the models list cache (used by ModelsTable)
      queryClient.setQueriesData(
        { queryKey: [ModelsQueryKeys.MODELS_LIST], exact: false },
        (oldData: FulfillmentListResponse) => {
          if (!oldData?.results || !Array.isArray(oldData.results)) {
            return oldData;
          }

          const updatedResults = oldData.results.map((fulfillment: Fulfillment) => {
            if (
              fulfillment.id === currentFulfillment?.id ||
              fulfillment.company_measurement_date === currentFulfillment?.company_measurement_date
            ) {
              return buildUpdatedFulfillment(fulfillment);
            }
            return fulfillment;
          });

          return {
            ...oldData,
            results: updatedResults,
          };
        }
      );
    },
    [companyId, currentFulfillment, queryClient]
  );

  const removeFulfillmentFromCaches = useCallback(() => {
    const removeMatchingFulfillment = (oldData: FulfillmentListResponse) => {
      if (!oldData?.results || !Array.isArray(oldData.results)) {
        return oldData;
      }

      const filteredResults = oldData.results.filter((fulfillment: Fulfillment) => {
        const matchesId = fulfillment.id === currentFulfillment?.id;
        const matchesMeasurementDate =
          fulfillment.company_measurement_date === currentFulfillment?.company_measurement_date;
        return !matchesId && !matchesMeasurementDate;
      });

      return {
        ...oldData,
        results: filteredResults,
      };
    };

    queryClient.setQueriesData(
      { queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId], exact: false },
      (oldData: FulfillmentListResponse) => removeMatchingFulfillment(oldData)
    );

    queryClient.setQueriesData(
      { queryKey: [ModelsQueryKeys.MODELS_LIST], exact: false },
      (oldData: FulfillmentListResponse) => removeMatchingFulfillment(oldData)
    );
  }, [companyId, currentFulfillment?.company_measurement_date, currentFulfillment?.id, queryClient]);

  const handleSave = useCallback(
    async (overrideData?: {
      supporting_due_date?: string | null;
      model_status?: number;
      is_waiting?: boolean;
      reviewerUsers?: UserProfile[];
      preparerUsers?: UserProfile[];
    }) => {
      try {
        if (!currentFulfillment?.id || !companyId || !valuationId) {
          throw new Error('Missing required data to save fulfillment');
        }

        // Extract reviewer and preparer IDs from override or currentFulfillment
        const reviewers = overrideData?.reviewerUsers ?? ((currentFulfillment.reviewers as UserProfile[]) || []);
        const preparers = overrideData?.preparerUsers ?? ((currentFulfillment.preparers as UserProfile[]) || []);
        const reviewerIds = reviewers
          .map((user: UserProfile) => user.id)
          .filter((id): id is number => id !== undefined);
        const preparerIds = preparers
          .map((user: UserProfile) => user.id)
          .filter((id): id is number => id !== undefined);

        const updateData: Partial<Fulfillment> = {
          supporting_due_date: overrideData?.supporting_due_date ?? tempFormData.supporting_due_date,
          model_status: overrideData?.model_status ?? tempFormData.model_status,
          is_waiting: overrideData?.is_waiting ?? tempFormData.is_waiting,
          reviewers: reviewerIds.length > 0 ? reviewerIds : [],
          preparers: preparerIds.length > 0 ? preparerIds : [],
        };

        await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
          companyId,
          valuationId,
          updateData as Fulfillment
        );

        // Update both caches with the new data
        // Exclude reviewers/preparers from updateData since we handle them separately with reviewerUsers/preparerUsers
        const cacheUpdateData: Partial<Fulfillment> = {
          supporting_due_date: updateData.supporting_due_date,
          model_status: updateData.model_status,
          is_waiting: updateData.is_waiting,
        };
        updateCaches({
          ...cacheUpdateData,
          reviewerUsers: overrideData?.reviewerUsers,
          preparerUsers: overrideData?.preparerUsers,
        });

        // Invalidate the valuation detail query to refresh the OperationsModal
        if (valuationId) {
          queryClient.invalidateQueries({
            queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
            exact: false,
          });
        }
        queryClient.invalidateQueries({
          queryKey: [ModelsQueryKeys.MODELS_LIST],
          exact: false,
        });

        enqueueSnackbar('Fulfillment saved successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to save fulfillment. Please try again.', { variant: 'error' });
        throw error;
      }
    },
    [companyId, currentFulfillment, valuationId, tempFormData, updateCaches, enqueueSnackbar, queryClient]
  );

  const handleSupportingDueDateChange = async (date: Date | null) => {
    // Convert to YYYY-MM-DD format for the API
    const formattedDate = date && !Number.isNaN(date.getTime()) ? date.toISOString().split('T')[0] : null;
    setTempFormData(prev => ({ ...prev, supporting_due_date: formattedDate }));
    // Auto-save after date change
    await handleSave({ supporting_due_date: formattedDate });
  };

  const commitModelStatusChange = async (statusId: string) => {
    const apiStatusValue = modelStatusMap[statusId as keyof typeof modelStatusMap];
    if (apiStatusValue !== undefined) {
      const statusValue = apiStatusValue as Fulfillment.model_status;
      setTempFormData(prev => ({ ...prev, model_status: statusValue }));
      await handleSave({ model_status: statusValue });
    }
  };

  const handleModelStatusChange = async (statusId: string) => {
    const currentStatusId =
      reverseModelStatusMap[
        tempFormData.model_status ?? (modelStatusMap[ModelStatusEnum.DRAFT] as keyof typeof reverseModelStatusMap)
      ] || ModelStatusEnum.DRAFT;

    if (statusId === currentStatusId) {
      return;
    }

    await commitModelStatusChange(statusId);
  };

  const handleIsWaitingChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsWaiting = event.target.checked;
    const previousIsWaiting = tempFormData.is_waiting;
    setTempFormData(prev => ({ ...prev, is_waiting: newIsWaiting }));
    // Auto-save after is_waiting change
    try {
      await handleSave({ is_waiting: newIsWaiting });
    } catch {
      // Revert state on failure to keep UI in sync with backend
      setTempFormData(prev => ({ ...prev, is_waiting: previousIsWaiting }));
    }
  };

  // Helper functions for display text
  const getReviewerDisplayText = () => {
    const reviewerUsers = currentFulfillment?.reviewers || [];
    if (reviewerUsers.length === 0) {
      return NO_REVIEWERS_ASSIGNED;
    }

    return reviewerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const getPreparerDisplayText = () => {
    const preparerUsers = currentFulfillment?.preparers || [];
    if (preparerUsers.length === 0) {
      return NO_PREPARERS_ASSIGNED;
    }

    return preparerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const handleAssignUsers = async (reviewerUsers: UserProfile[], preparerUsers: UserProfile[]) => {
    // Use handleSave to save reviewers and preparers (which also updates caches)
    await handleSave({ reviewerUsers, preparerUsers });
  };

  const handleDeleteModelSupport = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteModelSupport = useCallback(async () => {
    if (!companyId || !valuationId) {
      enqueueSnackbar('Missing required data to delete model support.', { variant: 'error' });
      return;
    }

    setIsDeletingModelSupport(true);
    try {
      await ApiService.apiDashboardCompanyOperationsFulfillmentDelete(companyId, valuationId);
      removeFulfillmentFromCaches();
      queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [ModelsQueryKeys.MODELS_LIST],
        exact: false,
      });
      if (valuationId) {
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        });
      }
      enqueueSnackbar('Model support deleted successfully.', { variant: 'success' });
      setIsDeleteConfirmOpen(false);
    } catch {
      enqueueSnackbar('Failed to delete model support. Please try again.', { variant: 'error' });
    } finally {
      setIsDeletingModelSupport(false);
    }
  }, [companyId, enqueueSnackbar, queryClient, removeFulfillmentFromCaches, valuationId]);

  const canAssignUsers = hasModelSupport && !fieldsDisabled;

  const handleAssignPreparerClick = () => {
    setAnchorButtonRef(preparerButtonRef.current);
    setIsAssignDialogOpen(true);
  };

  const handleAssignReviewerClick = () => {
    setAnchorButtonRef(reviewerButtonRef.current);
    setIsAssignDialogOpen(true);
  };

  return (
    <StyledBox>
      <Typography variant="h3" fontWeight={600} color="text.primary">
        Model Status
      </Typography>
      <Box display="flex" alignItems="center" gap={1} justifyContent="space-between" width="100%">
        {onSlcModelChange && <ModelTeamSwitch checked={isSlcModel ?? false} onChange={onSlcModelChange} />}
        {hasModelSupport && !readOnly && (
          <Tooltip
            title="When enabled, all fields in this section will be disabled. Use this to indicate the model is paused/waiting for external input."
            arrow
            placement="top">
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isWaiting}
                    onChange={handleIsWaitingChange}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      padding: '0px 8px',
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {isWaiting && <PauseCircleIcon fontSize="small" color="primary" />}
                    <Typography variant="body2" color={isWaiting ? 'primary.main' : 'text.secondary'}>
                      Is Waiting
                    </Typography>
                  </Box>
                }
                sx={{ mr: 0, ml: 'auto' }}
              />
            </Box>
          </Tooltip>
        )}
        {isSuperuser && hasModelSupport && (
          <Tooltip title="Delete Model Support">
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton size="small" onClick={handleDeleteModelSupport}>
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Box>
      <OverlayContainer>
        <BlurredContent hasModelSupport={hasModelSupport} readOnly={readOnly}>
          <Container>
            <SpacedContainer mt={0.5}>
              <ValuationStatusSelect
                id="model-status-select"
                title="Model Status:"
                statusOptions={Object.entries(reverseModelStatusMap).map(([, uiValue]) => ({
                  id: uiValue,
                  label: uiValue,
                }))}
                currentStatusId={
                  reverseModelStatusMap[
                    tempFormData.model_status ??
                        (modelStatusMap[ModelStatusEnum.DRAFT] as keyof typeof reverseModelStatusMap)
                  ] || ModelStatusEnum.DRAFT
                }
                onStatusChange={fieldsDisabled ? undefined : handleModelStatusChange}
                isLoading={isLoading}
                disabled={fieldsDisabled}
              />
            </SpacedContainer>
            <DateRow>
              <Typography fontWeight={600} fontSize="0.85rem">
                Supporting Due Date
              </Typography>
              <ConditionalLoader isLoading={isLoading} width={150}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MobileDatePicker
                    value={
                      tempFormData?.supporting_due_date ? new Date(`${tempFormData.supporting_due_date}T00:00:00`) : null
                    }
                    onChange={handleSupportingDueDateChange}
                    format="MM/dd/yyyy"
                    disabled={fieldsDisabled}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { maxWidth: 160, '& .MuiInputBase-input': { padding: '4px 8px' } },
                      },
                    }}
                  />
                  <CalendarMonthIcon fontSize="small" color={fieldsDisabled ? 'disabled' : 'primary'} />
                </Box>
              </ConditionalLoader>
            </DateRow>

            <StatusRow sx={{ opacity: isWaiting ? 0.5 : 1 }}>
              <FooterBox>
                <PersonnelRoleCard
                  label="Preparer"
                  name={getPreparerDisplayText()}
                  avatarData={currentFulfillment?.preparers as Profile[]}
                  isLoading={isLoading}
                  onEdit={canAssignUsers ? handleAssignPreparerClick : undefined}
                  editButtonRef={preparerButtonRef}
                  editTooltip="Assign Preparer"
                  editDisabled={isLoading}
                  avatarSx={{ fontWeight: 'bolder' }}
                  avatarStyle={{ backgroundColor: darken(getCalendarColorPalette().LABELS, 0.2) }}
                />
                <PersonnelRoleCard
                  label="Reviewer"
                  name={getReviewerDisplayText()}
                  avatarData={currentFulfillment?.reviewers as Profile[]}
                  isLoading={isLoading}
                  onEdit={canAssignUsers ? handleAssignReviewerClick : undefined}
                  editButtonRef={reviewerButtonRef}
                  editTooltip="Assign Reviewer"
                  editDisabled={isLoading}
                  avatarSx={{ fontWeight: 'bolder' }}
                  avatarStyle={{ backgroundColor: darken(getCalendarColorPalette().LABELS, 0.2) }}
                />
              </FooterBox>
            </StatusRow>
          </Container>
        </BlurredContent>

        {!hasModelSupport && !readOnly && (
          <BlurOverlay>
            <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
              Model Support Required
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
              A model support needs to be created before this area becomes available
            </Typography>
            <CreateModelSupportButton
              onClick={onCreateModelSupport}
              variant="contained"
              disabled={isCreatingModelSupport}>
              {isCreatingModelSupport ? 'Creating...' : 'Create Model Support'}
            </CreateModelSupportButton>
          </BlurOverlay>
        )}
      </OverlayContainer>
      <AssignUsersDialog
        open={isAssignDialogOpen}
        anchorEl={anchorButtonRef}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setAnchorButtonRef(null);
        }}
        onSave={handleAssignUsers}
        getUserDisplayName={getUserDisplayName}
        fulfillmentData={currentFulfillment}
        title="Assign Reviewers and Preparers"
        availableUsers={users}
      />
      <Modal
        open={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeletingModelSupport) {
            setIsDeleteConfirmOpen(false);
          }
        }}>
        <DeleteModalContainer>
          {isDeletingModelSupport && <LinearProgress color="error" />}
          <DeleteModalCloseButtonContainer>
            <IconButton
              size="small"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeletingModelSupport}
              aria-label="Close delete model support dialog">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DeleteModalCloseButtonContainer>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Delete Model Support
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <DeleteIcon fontSize="small" color="error" />
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this model support?
            </Typography>
          </Box>
          <DeleteModalActions>
            <Button
              variant="outlined"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeletingModelSupport}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteModelSupport}
              disabled={isDeletingModelSupport}>
              Delete
            </Button>
          </DeleteModalActions>
        </DeleteModalContainer>
      </Modal>
    </StyledBox>
  );
};

export default ModelStatus;
